import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Loader2 } from 'lucide-react';

export default function AdminCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [parties, setParties] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candDialog, setCandDialog] = useState(false);
  const [partyDialog, setPartyDialog] = useState(false);
  const [candForm, setCandForm] = useState({ full_name: '', position_id: '', party_id: '' });
  const [partyForm, setPartyForm] = useState({ name: '', abbreviation: '', color: '#3b82f6' });

  const load = async () => {
    try {
      const [c, p, pos] = await Promise.all([
        API.get('/admin/candidates'), API.get('/admin/parties'), API.get('/admin/positions'),
      ]);
      setCandidates(c.data); setParties(p.data); setPositions(pos.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createCandidate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/admin/candidates', {
        full_name: candForm.full_name,
        position_id: parseInt(candForm.position_id),
        party_id: parseInt(candForm.party_id),
      });
      setCandDialog(false);
      setCandForm({ full_name: '', position_id: '', party_id: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const createParty = async (e) => {
    e.preventDefault();
    try {
      await API.post('/admin/parties', partyForm);
      setPartyDialog(false);
      setPartyForm({ name: '', abbreviation: '', color: '#3b82f6' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="flex justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-candidates-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">Candidates & Parties</h1>

      {/* Parties */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-semibold text-slate-800">Parties</h2>
        <Dialog open={partyDialog} onOpenChange={setPartyDialog}>
          <DialogTrigger asChild><Button data-testid="add-party-btn" variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Add Party</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add Party</DialogTitle></DialogHeader>
            <form onSubmit={createParty} className="space-y-4">
              <div><Label>Name</Label><Input data-testid="party-name-input" value={partyForm.name} onChange={e => setPartyForm({...partyForm, name: e.target.value})} required /></div>
              <div><Label>Abbreviation</Label><Input data-testid="party-abbr-input" value={partyForm.abbreviation} onChange={e => setPartyForm({...partyForm, abbreviation: e.target.value})} required /></div>
              <div><Label>Color</Label><Input data-testid="party-color-input" type="color" value={partyForm.color} onChange={e => setPartyForm({...partyForm, color: e.target.value})} /></div>
              <Button data-testid="submit-party-btn" type="submit" className="w-full bg-slate-900 hover:bg-slate-800">Create Party</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {parties.map(p => (
          <Badge key={p.id} style={{ backgroundColor: p.color, color: '#fff' }} className="text-sm px-3 py-1">
            {p.name} ({p.abbreviation})
            <button onClick={async () => { await API.delete(`/admin/parties/${p.id}`); load(); }} className="ml-2 hover:opacity-70">
              <Trash2 className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Candidates */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-semibold text-slate-800">Candidates</h2>
        <Dialog open={candDialog} onOpenChange={setCandDialog}>
          <DialogTrigger asChild><Button data-testid="add-candidate-btn" variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Add Candidate</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add Candidate</DialogTitle></DialogHeader>
            <form onSubmit={createCandidate} className="space-y-4">
              <div><Label>Full Name</Label><Input data-testid="cand-name-input" value={candForm.full_name} onChange={e => setCandForm({...candForm, full_name: e.target.value})} required /></div>
              <div>
                <Label>Position</Label>
                <Select value={candForm.position_id} onValueChange={v => setCandForm({...candForm, position_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>{positions.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Party</Label>
                <Select value={candForm.party_id} onValueChange={v => setCandForm({...candForm, party_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select party" /></SelectTrigger>
                  <SelectContent>{parties.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button data-testid="submit-candidate-btn" type="submit" className="w-full bg-slate-900 hover:bg-slate-800">Create Candidate</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <table className="w-full data-table">
            <thead><tr><th>Name</th><th>Party</th><th>Actions</th></tr></thead>
            <tbody>
              {candidates.map(c => (
                <tr key={c.id}>
                  <td className="font-medium text-slate-900">{c.full_name}</td>
                  <td>
                    <Badge style={{ backgroundColor: c.party_color || '#94a3b8', color: '#fff' }} className="text-xs">
                      {c.party_name}
                    </Badge>
                  </td>
                  <td>
                    <Button data-testid={`delete-candidate-${c.id}`} variant="ghost" size="sm" onClick={async () => { await API.delete(`/admin/candidates/${c.id}`); load(); }}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
